import React, { useState, useEffect } from 'react';
import { DollarSign, Save, AlertCircle, CheckCircle, User, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '@solana/wallet-adapter-react';
import { sign } from 'tweetnacl';
import bs58 from 'bs58';
import { formatSOL, formatSOLAmount } from '../utils/formatSol';

// Handle ResizeObserver errors
const suppressResizeObserverError = () => {
    const resizeObserverErrorHandler = (e: ErrorEvent) => {
        if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
            e.preventDefault();
            e.stopPropagation();
            return false;
        }
        return true;
    };

    window.addEventListener('error', resizeObserverErrorHandler);
    return () => window.removeEventListener('error', resizeObserverErrorHandler);
};

interface CreatorProfile {
    id: string;
    username: string;
    email: string;
    monthlyPrice?: number;
    yearlyPrice?: number;
    paymentWalletAddress?: string;
    bio?: string;
    role: string;
    subscribersCount?: number;
    totalEarnings?: number;
}

const CreatorSettings: React.FC = () => {
    const { user, token } = useAuth();
    const { publicKey, signMessage } = useWallet();
    const [profile, setProfile] = useState<CreatorProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [showSignatureDialog, setShowSignatureDialog] = useState(false);
    const [pendingWalletAddress, setPendingWalletAddress] = useState('');

    const [formData, setFormData] = useState({
        monthlyPrice: '',
        yearlyPrice: '',
        paymentWalletAddress: '',
        bio: ''
    });

    useEffect(() => {
        loadProfile();
    }, []);

    useEffect(() => {
        // Suppress ResizeObserver errors
        const cleanup = suppressResizeObserverError();
        return cleanup;
    }, []);

    const loadProfile = async () => {
        try {
            const response = await fetch('/api/users/profile', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setProfile(data.user);
                setFormData({
                    monthlyPrice: data.user.monthlyPrice?.toString() || '',
                    yearlyPrice: data.user.yearlyPrice?.toString() || '',
                    paymentWalletAddress: data.user.paymentWalletAddress || '',
                    bio: data.user.bio || ''
                });
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            setError('Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const updateData = {
                monthlyPrice: formData.monthlyPrice ? parseFloat(formData.monthlyPrice) : undefined,
                yearlyPrice: formData.yearlyPrice ? parseFloat(formData.yearlyPrice) : undefined,
                paymentWalletAddress: formData.paymentWalletAddress || undefined,
                bio: formData.bio || undefined
            };

            // Check if payment wallet address is being updated
            const isWalletAddressChanged = updateData.paymentWalletAddress &&
                updateData.paymentWalletAddress !== profile?.paymentWalletAddress;

            if (isWalletAddressChanged) {
                // Show signature dialog
                setPendingWalletAddress(updateData.paymentWalletAddress || '');
                setShowSignatureDialog(true);
                setSaving(false);
                return;
            }

            // Continue with regular update if no wallet address change
            await submitUpdate(updateData);
        } catch (error) {
            console.error('Error updating settings:', error);
            setError('Failed to update settings');
            setSaving(false);
        }
    };

    const submitUpdate = async (updateData: any, signature?: string, message?: string) => {
        try {
            const payload = { ...updateData };
            if (signature && message) {
                payload.signature = signature;
                payload.message = message;
            }

            const response = await fetch('/api/kyc/creator-profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setSuccess('Settings updated successfully!');
                setShowSignatureDialog(false);
                setPendingWalletAddress('');

                // Use setTimeout to prevent rapid DOM changes that cause ResizeObserver errors
                setTimeout(() => {
                    loadProfile(); // Refresh profile data
                }, 100);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to update settings');
            }
        } catch (error) {
            console.error('Error updating settings:', error);
            setError('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    const handleSignWalletUpdate = async () => {
        if (!publicKey || !signMessage) {
            setError('Wallet not connected or does not support signing');
            return;
        }

        try {
            setSaving(true);
            const message = `Update payment wallet to: ${pendingWalletAddress}`;
            const messageBytes = new TextEncoder().encode(message);
            const signature = await signMessage(messageBytes);

            // Convert signature to base58 string
            const signatureString = Array.from(signature).map(b => b.toString(16).padStart(2, '0')).join('');

            const updateData = {
                monthlyPrice: formData.monthlyPrice ? parseFloat(formData.monthlyPrice) : undefined,
                yearlyPrice: formData.yearlyPrice ? parseFloat(formData.yearlyPrice) : undefined,
                paymentWalletAddress: pendingWalletAddress,
                bio: formData.bio || undefined
            };

            await submitUpdate(updateData, signatureString, message);
        } catch (error) {
            console.error('Error signing wallet update:', error);
            setError('Failed to sign wallet update. Please try again.');
            setSaving(false);
        }
    };

    const handleCancelSignature = () => {
        setShowSignatureDialog(false);
        setPendingWalletAddress('');
        setSaving(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Special handling for price fields
        if (name === 'monthlyPrice' || name === 'yearlyPrice') {
            // Allow empty string
            if (value === '') {
                setFormData({
                    ...formData,
                    [name]: ''
                });
                return;
            }

            // Only allow digits and one decimal point
            if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
                return;
            }

            // Don't allow multiple decimal points
            if ((value.match(/\./g) || []).length > 1) {
                return;
            }

            // Limit decimal places to 2
            if (value.includes('.')) {
                const decimalPart = value.split('.')[1];
                if (decimalPart && decimalPart.length > 2) {
                    return;
                }
            }

            // Validate minimum value 0.01 if not empty
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 0 && numValue < 0.01) {
                return;
            }

            setFormData({
                ...formData,
                [name]: value
            });
            return;
        }

        // Default handling for other fields
        setFormData({
            ...formData,
            [name]: value
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Loading settings...</p>
                </div>
            </div>
        );
    }

    if (!profile || profile.role !== 'creator') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Creator Access Required</h2>
                    <p className="text-gray-600">You need to be a creator to access these settings.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Creator Settings</h1>
                    <p className="text-gray-600">Manage your subscription pricing and profile</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center">
                            <User className="w-8 h-8 text-blue-500" />
                            <div className="ml-4">
                                <p className="text-sm text-gray-600">Subscribers</p>
                                <p className="text-2xl font-semibold text-gray-900">{profile.subscribersCount || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center">
                            <CreditCard className="w-8 h-8 text-green-500" />
                            <div className="ml-4">
                                <p className="text-sm text-gray-600">Total Earnings</p>
                                <p className="text-2xl font-semibold text-gray-900">{formatSOL(profile.totalEarnings || 0)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center">
                            <DollarSign className="w-8 h-8 text-purple-500" />
                            <div className="ml-4">
                                <p className="text-sm text-gray-600">Monthly Price</p>
                                <p className="text-2xl font-semibold text-gray-900">{formatSOL(profile.monthlyPrice || 0)}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Settings Form */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h2 className="text-xl font-semibold text-gray-900">Subscription Settings</h2>
                        <p className="text-gray-600 mt-1">Set your subscription prices and payment details</p>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {/* Alert Messages */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                                <div className="flex">
                                    <AlertCircle className="w-5 h-5 text-red-400 mr-2 mt-0.5" />
                                    <span className="text-red-800">{error}</span>
                                </div>
                            </div>
                        )}

                        {success && (
                            <div className="bg-green-50 border border-green-200 rounded-md p-4">
                                <div className="flex">
                                    <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5" />
                                    <span className="text-green-800">{success}</span>
                                </div>
                            </div>
                        )}

                        {/* Pricing Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="monthlyPrice" className="block text-sm font-medium text-gray-700 mb-2">
                                    Monthly Price (SOL)
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="monthlyPrice"
                                        name="monthlyPrice"
                                        value={formData.monthlyPrice}
                                        onChange={handleChange}
                                        placeholder="0.01"
                                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-500 text-sm">SOL</span>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Price for monthly subscriptions (2 decimal places, minimum 0.01)
                                    {formData.monthlyPrice && (
                                        <span className="block text-green-600 text-xs mt-1">
                                            ✓ Current: {formData.monthlyPrice} SOL
                                        </span>
                                    )}
                                </p>
                            </div>

                            <div>
                                <label htmlFor="yearlyPrice" className="block text-sm font-medium text-gray-700 mb-2">
                                    Yearly Price (SOL)
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="yearlyPrice"
                                        name="yearlyPrice"
                                        value={formData.yearlyPrice}
                                        onChange={handleChange}
                                        placeholder="0.01"
                                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-500 text-sm">SOL</span>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Price for yearly subscriptions (2 decimal places, minimum 0.01)
                                    {formData.yearlyPrice && (
                                        <span className="block text-green-600 text-xs mt-1">
                                            ✓ Current: {formData.yearlyPrice} SOL
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Payment Wallet */}
                        <div>
                            <label htmlFor="paymentWalletAddress" className="block text-sm font-medium text-gray-700 mb-2">
                                Payment Wallet Address
                            </label>
                            <input
                                type="text"
                                id="paymentWalletAddress"
                                name="paymentWalletAddress"
                                value={formData.paymentWalletAddress}
                                onChange={handleChange}
                                placeholder="Your Solana wallet address for receiving payments"
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Solana wallet address where subscription payments will be sent</p>
                        </div>

                        {/* Bio */}
                        <div>
                            <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                                Creator Bio
                            </label>
                            <textarea
                                id="bio"
                                name="bio"
                                rows={4}
                                value={formData.bio}
                                onChange={handleChange}
                                placeholder="Tell your subscribers about yourself and your content..."
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">This will be displayed on your profile</p>
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Save className="w-5 h-5 mr-2" />
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Wallet Signature Dialog */}
                {showSignatureDialog && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Wallet Signature Required</h3>
                                <p className="text-gray-600 text-sm">
                                    You're updating your payment wallet address. Please sign this message to verify your wallet ownership.
                                </p>
                            </div>

                            <div className="bg-gray-50 rounded-md p-4 mb-4">
                                <p className="text-sm font-medium text-gray-700 mb-1">New Payment Wallet:</p>
                                <p className="text-sm text-gray-600 break-all">{pendingWalletAddress}</p>
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                                <p className="text-sm text-yellow-800">
                                    <AlertCircle className="w-4 h-4 inline mr-1" />
                                    This will update where your subscription payments are sent.
                                </p>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={handleCancelSignature}
                                    disabled={saving}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSignWalletUpdate}
                                    disabled={saving || !publicKey}
                                    className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {saving ? 'Signing...' : 'Sign & Update'}
                                </button>
                            </div>

                            {!publicKey && (
                                <p className="text-red-600 text-sm mt-2 text-center">
                                    Please connect your wallet to continue
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreatorSettings;